import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { adaptEchartsOptions, BaseEchartsDirective } from '@examdojo/core/widget';
import { EChartsOption } from 'echarts';
import { ScatterChart } from 'echarts/charts';
import { GridComponent, MarkAreaComponent, MarkLineComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

import { HOVER_VALUE_CHART_COLOR, type HistogramOverlay } from '../../measures/measures/measures-highlight.util';
import { buildOverlaySeries, fmt } from '../../../shared/chart-shared';
import {
  DEFAULT_STRIP_PLOT_DISPLAY,
  type StripPlotDisplayConfig,
  type StripPlotParams,
} from './strip-plot-chart.model';

echarts.use([ScatterChart, GridComponent, TooltipComponent, MarkLineComponent, MarkAreaComponent]);

@Component({
  selector: 'dojo-strip-plot-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './strip-plot-chart.component.html',
  styleUrl: './strip-plot-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block w-full h-full' },
})
export class StripPlotChartComponent extends BaseEchartsDirective {
  readonly params = input.required<StripPlotParams>();
  readonly display = input<StripPlotDisplayConfig>({});
  readonly overlay = input<HistogramOverlay>({ markLines: [], markAreas: [] });
  readonly selectedOutlierValue = input<number | null>(null);
  readonly hoveredValue = input<number | null>(null);

  readonly outlierSelect = output<number>();

  readonly chartOptions = computed<EChartsOption>(() => {
    const display = { ...DEFAULT_STRIP_PLOT_DISPLAY, ...this.display() };
    const columns = this.params().columns;
    const overlay = this.overlay();
    const selectedValue = this.selectedOutlierValue();
    const hoveredValue = this.hoveredValue();

    const categories: string[] = [];
    const rawData: Array<{ value: [number, number]; originalValue: number }> = [];

    for (let colIdx = 0; colIdx < columns.length; colIdx++) {
      const col = columns[colIdx];
      categories.push(col.name);

      // Stack dots at the same value by offsetting within the category band
      const sorted = [...col.data].sort((a, b) => a - b);

      const startIdx = rawData.length;
      for (const value of sorted) {
        rawData.push({ value: [value, colIdx], originalValue: value });
      }

      // Center stacks of identical values around the category index
      const valueCounts = new Map<number, { total: number; current: number }>();
      for (const value of sorted) {
        const entry = valueCounts.get(value);
        if (entry) {
          entry.total++;
        } else {
          valueCounts.set(value, { total: 1, current: 0 });
        }
      }

      for (let i = startIdx; i < rawData.length; i++) {
        const value = rawData[i].value[0];
        const entry = valueCounts.get(value)!;
        const offset = (entry.current - (entry.total - 1) / 2) * 0.06;
        rawData[i].value[1] = colIdx + offset;
        entry.current++;
      }
    }

    const bold = (v: string | number) => `<span style="font-weight:600;color:#111827">${v}</span>`;

    const scatterData = rawData.map((d) => {
      const isSelected = selectedValue !== null && selectedValue !== undefined && d.originalValue === selectedValue;
      const isHovered = hoveredValue !== null && d.originalValue === hoveredValue;
      return {
        value: d.value,
        itemStyle: isSelected ? { color: '#fbbf24' } : isHovered ? { color: HOVER_VALUE_CHART_COLOR } : undefined,
      };
    });

    const series: NonNullable<EChartsOption['series']> = [
      {
        type: 'scatter',
        name: 'Strip Plot',
        data: scatterData,
        symbolSize: display.dotSize ?? 7,
        itemStyle: {
          color: display.dotColor ?? '#6366f1',
        },
      },
      buildOverlaySeries(overlay),
    ];

    return {
      tooltip: {
        show: true,
        trigger: 'item',
        confine: true,
        backgroundColor: '#fff',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        extraCssText: 'box-shadow: 0 2px 8px rgba(0,0,0,0.08);',
        padding: [10, 14],
        textStyle: { fontSize: 12, color: '#374151' },
        formatter: (params: unknown) => {
          const p = params as { seriesName?: string; value?: number[] };
          if (p.seriesName !== 'Strip Plot' || !p.value) {
            return '';
          }
          return [
            `<div style="margin-bottom:4px;font-weight:600;color:#6366f1">Data Point</div>`,
            `<div style="display:flex;justify-content:space-between;gap:16px"><span style="color:#6b7280">Value</span>${bold(
              fmt(p.value[0]),
            )}</div>`,
          ].join('');
        },
      },
      grid: {
        top: 25,
        bottom: 25,
        left: 60,
        right: 60,
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        name: 'Value',
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: { fontSize: 12, color: '#6b7280' },
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#d1d5db' } },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
        axisLabel: { fontSize: 12, color: '#6b7280' },
      },
      yAxis: {
        type: 'category',
        data: categories,
        axisLabel: { show: categories.length > 1 },
        axisTick: { show: false },
        axisLine: { show: false },
        splitLine: { show: false },
      },
      series,
    };
  });

  readonly adaptedOptions = computed(() => adaptEchartsOptions(this.chartOptions(), this.document, this.isDark()));

  onChartClick(event: { seriesType?: string; seriesName?: string; data?: unknown }): void {
    if (event.seriesType !== 'scatter' || event.seriesName !== 'Strip Plot') {
      return;
    }
    const d = event.data as { value?: number[] } | undefined;
    if (!d?.value) {
      return;
    }
    this.outlierSelect.emit(d.value[0]);
  }
}
